import { Component, Input, ViewChild } from '@angular/core';
import { Platform, ModalController, PopoverController, Nav, Events, Select } from 'ionic-angular';
import { StatusBar } from '@ionic-native/status-bar';
import { SplashScreen } from '@ionic-native/splash-screen';
import { Deeplinks } from '@ionic-native/deeplinks';

import { User }     from '../providers/user/user';
import { Utils }    from '../providers/utils/utils';
import { Api }      from '../providers/api/api';
import { IPTV }     from '../providers/iptv/iptv';
import { Gateway }  from '../providers/gateway/gateway';
import { Deeplink } from '../providers/deeplink/deeplink';
import { Services } from '../providers/services/services';

import { Login }      from '../pages/login/login';

import { Search }     from '../pages/search/search';
import { Settings }   from '../pages/settings/settings';
import { Remote }     from '../pages/remote/remote';
import { Disconnect } from '../pages/disconnect/disconnect';

@Component({
  templateUrl: 'app.component.html'
})

export class App {
    @ViewChild(Nav) navCtrl: Nav;
    
    @ViewChild('selectResolution') selectProfile: Select;
    @ViewChild('selectBox') selectSource: Select;

    @Input() title: string;
    
    public IPTVReady : boolean = false;
    public live: any = {};
    public output : any = {};
    public interval: any;
    public boxChanging : boolean = false;
    public form: any = {};
    public platformPause : boolean = false;
    public serviceInProgress : boolean = false;
    public serviceInterval : any;
    public servicePause : boolean = false;

    /*
    *    App root
    */
    public rootPage: any;

    /*
    *    Constructor
    */
    constructor(
        public platform: Platform,
        private splashScreen: SplashScreen,
        public user: User, 
        public events: Events,
        public IPTV: IPTV,
        public utils: Utils,
        public api: Api,
        public gateway: Gateway,
        public statusBar: StatusBar,
        public popoverCtrl: PopoverController,
        public modalCtrl: ModalController,
        private deeplinks: Deeplinks,
        public deepLink: Deeplink,
        public services: Services
    ) {

        this.rootPage = Login;

        this.platform.ready().then(() => {

            this.utils.log('App::platform::ready');

            // Set Plateform ready
            this.events.publish('platform:ready');

            // Splashcreen and status and orientation
            if (this.utils.isMobile()) {
                this.splashScreen.hide();
                this.statusBar.show();
                this.statusBar.overlaysWebView(false);
                this.statusBar.backgroundColorByHexString('#000');
            }

            let that = this;

            setTimeout(function() {
                // Services init
                that.servicesInit();
            });

            this.subscribeDeepLinks();

            // On pause
            this.platform.pause.subscribe(() => {
                this.utils.log('App::platform::pause()');
                this.events.publish('platform:pause');
                this.gateway.stop();
                this.platformPause  = true;
                if (this.serviceInterval) clearInterval(that.serviceInterval);
            }, false);

            // On resume
            this.platform.resume.subscribe(() => {
                this.utils.log('App::platform::resume()');
                this.events.publish('platform:resume');
                this.gateway.restart();
                this.platformPause  = false;

                if (this.serviceInterval) clearInterval(that.serviceInterval);
                this.serviceCheck();
                this.serviceInterval = setInterval(function() {
                    that.serviceCheck();
                },5000);
            });
            
            // Init
            this.eventsInit();
        });
    }

    /*
    *    Watch for deeplink changes
    */
    subscribeDeepLinks(doProcess : boolean = false) 
    {
        if (this.utils.isBrowser()) return false;

        // Deeplinks detection
        this.deeplinks.route({}).subscribe(match => {
        }, route => {
            if (route && route.$link) {
                this.utils.log('App::subscribeDeepLinks::await()');
                this.IPTV.isRemoting = true;
                this.setLoading();
                this.deepLink.awaitingDeeplink = route.$link.url;
                this.subscribeDeepLinks();
            } 
        });
    }

    /*
    *    Process awaiting deeplink
    */
    processWaitingDeeplink() 
    {
        if (this.deepLink.awaitingDeeplink) {
            this.deepLink.setRoute(this.deepLink.awaitingDeeplink);
            this.deepLink.process();
            this.deepLink.awaitingDeeplink = '';
        }
    }

    /*
    *    Global event manager
    */
    eventsInit()
    {
        this.events.subscribe('app:goToLogin', () => {
            this.showPage(Login);
        });

        this.events.subscribe('iptv:on', () => {
            this.utils.log('App::IPTV::ON');
            this.IPTVReady = true;
            this.setDefaultForm();
            this.processWaitingDeeplink();
        });

        this.events.subscribe('iptv:off', () => {
            this.utils.log('App::IPTV::OFF');
            this.IPTVReady = false;
        });

        this.events.subscribe('live:loading', () => { 
            this.setLoading();
        });

        this.events.subscribe('live:success', (text = '') => { 
            this.setSuccess(text);
        });

        this.events.subscribe('live:error', (text = '') => { 
            this.setError(text);
        });

        this.events.subscribe('user:forceDisconnect', () => {
            this.user.isDisconnecting = true;
            this.showPage(Disconnect);
        });

        this.events.subscribe('service:pause', () => {
            this.servicePause = true;
        });

        this.events.subscribe('service:resume', () => {
            this.servicePause = false;
        });

    }
     
    /*
    *    IPTV Services init
    */
    servicesInit() {

        // Gateway detection
        let that = this;
        this.gateway.init();
        this.gateway.restart();

        this.serviceCheck();

        this.serviceInterval = setInterval(function() {
            that.serviceCheck();
        },5000);
    }

    serviceCheck() {
        if (!this.IPTVReady) return false;
        if (this.platformPause) return false;
        if (this.serviceInProgress) return false;
        if (this.servicePause) return false;

        this.serviceInProgress = true;
        this.services.check().then(() => {

            this.utils.log('App::serviceCheck()::Success');

            if (this.deepLink.awaitingDeeplink && localStorage.getItem('token')) {
                if (this.deepLink.awaitingDeeplink) {
                    this.processWaitingDeeplink();
                }
            }

            this.serviceInProgress = false;
        }, (err) => {

            this.utils.log('App::serviceCheck()::Error');

            if (err == 'DEALLOCATE') {
                this.IPTV.resetData();
                if (this.user.isGuest()) {
                    this.user.resetUserData();
                }

                this.IPTVReady = false;
                this.showPage(Login);
            }
            this.serviceInProgress = false;
        });
    }
   
    /*
    *    Redirect to Page
    */
    showPage(Page) {
        this.navCtrl.setRoot(Page);
    }

    presentSearch() {
        let profileModal = this.modalCtrl.create(Search, undefined, { cssClass: "modal-fullscreen" });
        profileModal.present();
    }

    presentSettings(myEvent) {
        let popover = this.popoverCtrl.create(Settings);
        popover.present({
          ev: myEvent
        });
        popover.onDidDismiss(() => {
          this.events.publish('settings:closed');
        });
    }

    presentRemote(myEvent) {
        let popover = this.popoverCtrl.create(Remote);
        popover.present({
          ev: myEvent
        });
        popover.onDidDismiss(() => {
          this.events.publish('remote:closed');
        });
    }

    setLoading() {
        this.setLive('','grey','',true);
    }

    setSuccess(text = '') {
        this.setLive(text, 'success', 'checkmark-circle');
    }

    setError (text = '') {
        this.setLive(text, 'danger', 'close-circle');
    }

    setLive(text = '', color = '', icon = '', noTimer = false) {

         if (this.live.timer) clearTimeout(this.live.timer);

        this.live.name  = ( text ? this.utils.truncate(text,15,'..') : ' ');
        this.live.color = color;
        this.live.icon  = icon;

        if (!noTimer) {
            this.live.timer = setTimeout( () => {
             this.live = {};
            }, 1000);
        }
    }

    setSpinner(text = '') {
      this.live.name = ' ';
    }

    setDefaultForm() {
        this.form = {
            resolutionID : this.IPTV.resolution.id,
            boxID : this.IPTV.box.id
        }
    }
    
    changeBox(boxID) {

        this.utils.load();

        this.IPTV.setSource(boxID).then(() => {
            this.utils.unload();
            this.events.publish('live:success');
            this.events.publish('app:refresh');
        }, (err) => {
            this.utils.unload();
        });
    }

    

     changeResolution(id) {
        this.utils.load();

        this.IPTV.setResolution(id).then(() => {
            this.events.publish('live:success');
            this.utils.unload();
        },(err) => {
            this.utils.unload();
            this.utils.error(err);
        });
    }

    /*
    *    Open Selects
    */
    openProfileSelect() {
        this.setDefaultForm();

        let that = this;
        setTimeout(function() {
            that.selectProfile.open();
        },100);
    }
    openSourceSelect() {
        if (this.IPTV.boxs.length < 2) return false;

        this.setDefaultForm();

        let that = this;
        setTimeout(function() {
            if (that.IPTV.boxs.length > 1)  that.selectSource.open();
        },100);
    }

    navPop() {
        if (this.navCtrl.canGoBack()) this.navCtrl.pop();
    }
}




// WEBPACK FOOTER //
// ./src/app/app.component.ts